import React, { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import {
  createInvitation,
  listPendingInvitations,
  revokeInvitation,
  ROLES,
} from "../../../services/users";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { UserPlus, Trash2 } from "lucide-react";

export default function InviteUser() {
  const { profile, isSuperAdmin } = useAuth();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(ROLES.USER);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [pendingInvites, setPendingInvites] = useState([]);

  const loadPendingInvites = async () => {
    try {
      const invites = await listPendingInvitations();
      setPendingInvites(invites);
    } catch (err) {
      console.error("Failed to load invites:", err);
    }
  };

  useEffect(() => {
    loadPendingInvites();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      await createInvitation({
        email: email.toLowerCase().trim(),
        role,
        invitedBy: profile.email,
      });
      setSuccess(`Invitation sent to ${email}`);
      setEmail("");
      setRole(ROLES.USER);
      await loadPendingInvites();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (inviteEmail) => {
    if (!window.confirm(`Revoke invitation for ${inviteEmail}?`)) return;
    try {
      await revokeInvitation(inviteEmail);
      await loadPendingInvites();
    } catch (err) {
      alert("Failed to revoke: " + err.message);
    }
  };

  const getRoleBadgeVariant = (role) => {
    if (role === "super-admin") return "purple";
    if (role === "admin") return "default";
    return "secondary";
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Invite User</CardTitle>
          <CardDescription>
            Create an invitation for a new team member. They will need to be added
            to Firebase Authentication separately, then their role will be assigned on first login.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex gap-4 items-end flex-wrap">
            <div className="flex-1 min-w-[200px] space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="user@example.com"
              />
            </div>
            <div className="w-32 space-y-2">
              <Label htmlFor="role">Role</Label>
              <select
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value={ROLES.USER}>User</option>
                <option value={ROLES.ADMIN}>Admin</option>
                {isSuperAdmin && (
                  <option value={ROLES.SUPER_ADMIN}>Super Admin</option>
                )}
              </select>
            </div>
            <Button type="submit" disabled={loading}>
              <UserPlus className="h-4 w-4 mr-2" />
              {loading ? "Sending..." : "Send Invite"}
            </Button>
          </form>

          {error && (
            <div className="mt-4 text-sm text-destructive bg-destructive/10 p-3 rounded-md">
              {error}
            </div>
          )}
          {success && (
            <div className="mt-4 text-sm text-green-600 bg-green-50 p-3 rounded-md">
              {success}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pending Invitations</CardTitle>
          <CardDescription>
            {pendingInvites.length} pending invitation{pendingInvites.length !== 1 ? "s" : ""}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pendingInvites.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No pending invitations
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Invited By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingInvites.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-medium">{inv.email}</TableCell>
                    <TableCell>
                      <Badge variant={getRoleBadgeVariant(inv.role)}>
                        {inv.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {inv.invitedBy}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {inv.createdAt?.toDate?.()?.toLocaleDateString() || "N/A"}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleRevoke(inv.email)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
